import { useCallback, useMemo, useState } from "react";

import { formatTimestamp } from "~/utils/helpers";
import { useRect } from "~/hooks/useRect";
import { useLabels } from "~/hooks/useLabels";
import { getNoteColorConfig, toPx } from "~/utils/components";

import { Icon } from "../icon/Icon";
import { NoteActionsModal } from "../noteActionsModal/NoteActionsModal";
import { HtmlContent } from "../htmlContent/HtmlContent";
import { DEFAULT_NOTE_COLOR } from "../../constants";
import { Link } from "../link/Link";

import "./NoteCard.css";

import type { CSSProperties, SyntheticEvent, ReactNode } from "react";
import type { ActionsProp, ColorProp } from "../components";
import type { NoteActionsModalProps } from "../noteActionsModal/NoteActionsModal";

export type NoteColor = Extract<
  ColorProp,
  "white" | "green" | "cyan" | "purple" | "orange" | "red" | "yellow"
>;

export interface NoteCardProps {
  actions?: ActionsProp;
  children?: ReactNode;
  color?: Maybe<NoteColor>;
  content?: string;
  href?: string;
  footnote?: string;
  isFavorited?: boolean;
  truncate?: boolean;
  modalPostTitle: NoteActionsModalProps["postTitle"];
  modalTitle: NoteActionsModalProps["title"];
  startTime?: number;
  endTime?: number;
  timestampHref?: string;
  onColorChange?: NoteActionsModalProps["onColorChange"];
  onTimestampClick?: () => void;
  onFavorite?: () => void;
}

const TRUNCATE_THRESHOLD = 320;

const DEFAULT_LABELS: Record<string, string> = {
  "common.cta.actions": "Actions",
  "common.cta.favorite": "Favorite",
  "note.cta.collapse": "Collapse note",
  "note.cta.details": "View Note Details",
  "note.cta.expand": "Expand note",
};

export function NoteCard({
  actions,
  children,
  color,
  content,
  href,
  footnote,
  isFavorited,
  truncate,
  modalPostTitle,
  modalTitle,
  startTime,
  endTime,
  timestampHref,
  onColorChange,
  onTimestampClick,
  onFavorite,
}: NoteCardProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  // Truncation logic
  const [isTruncated, setIsTruncated] = useState(truncate);
  const [contentRef, { height }] = useRect<HTMLDivElement>(truncate);
  const shouldTruncate = useMemo(() => {
    if (!truncate || !contentRef.current || !height) return false;
    return contentRef.current?.scrollHeight >= TRUNCATE_THRESHOLD;
  }, [truncate, contentRef, height]);

  const { background, text, metadata, metadataActive, border } =
    getNoteColorConfig(color ?? DEFAULT_NOTE_COLOR);

  const customProperties = {
    "--note-color-background": background,
    "--note-color-border": border,
    "--note-color-text": text,
    "--note-color-timestamp": metadata,
    "--note-color-timestamp-active": metadataActive,
    "--note-truncate-threshold":
      truncate && isTruncated ? toPx(TRUNCATE_THRESHOLD) : "100%",
  } as CSSProperties;

  const computeActions = () => {
    if (typeof actions === "function") {
      return actions({
        close() {
          setIsActionsOpen(false);
        },
      });
    }

    return actions;
  };

  const handleTimestampClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      onTimestampClick?.();
    },
    [onTimestampClick],
  );

  const handleFavoriteClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    onFavorite?.();
  };

  const handleActionsClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.stopPropagation();
    setIsActionsOpen(true);
  };

  const handleRequestCloseSheet = useCallback(() => {
    setIsActionsOpen(false);
  }, []);

  const handleTruncateToggle = useCallback(() => {
    setIsTruncated((v) => !v);
  }, []);

  const renderedTimestampButton = useMemo(() => {
    if (startTime == null || endTime == null) return null;

    const children = (
      <>
        <Icon icon="play" variant="solid" size="var(--size-1-5)" />
        {`${formatTimestamp(startTime)}-${formatTimestamp(endTime)}`}
      </>
    );

    if (timestampHref) {
      return (
        <Link
          to={timestampHref}
          className="NoteCard__button"
          nativeBehavior="replacePresentFullscreen"
          nativeStencil={{
            propsJSON: "{}",
            type: "session",
          }}
        >
          {children}
        </Link>
      );
    }

    return (
      <div onClick={handleTimestampClick} className="NoteCard__button">
        {children}
      </div>
    );
  }, [startTime, endTime, timestampHref, handleTimestampClick]);

  return (
    <>
      <div
        className="NoteCard"
        style={customProperties}
        aria-expanded={!isTruncated}
      >
        <div className="NoteCard__header">
          {renderedTimestampButton}

          <button
            aria-label={t("common.cta.favorite")}
            className="NoteCard__favorite"
            onClick={handleFavoriteClick}
          >
            <Icon
              icon="star"
              size="lg"
              variant={isFavorited ? "solid" : "regular"}
            />
          </button>

          {Boolean(actions) && (
            <button
              aria-label={t("common.cta.actions")}
              className="NoteCard__actions"
              onClick={handleActionsClick}
            >
              <Icon size="md" icon="ellipsis" />
            </button>
          )}
        </div>

        <div ref={contentRef} className="NoteCard__content">
          {content && <HtmlContent html={content} />}

          {children}

          {footnote && (
            <div className="NoteCard__content-footnote">{footnote}</div>
          )}

          {shouldTruncate && (
            <button
              className="NoteCard__truncate"
              onClick={handleTruncateToggle}
            >
              {isTruncated ? t("note.cta.expand") : t("note.cta.collapse")}
            </button>
          )}
        </div>

        {href && (
          <Link
            to={href}
            className="NoteCard__button NoteCard__button--full"
            style={{ width: "100%", marginTop: "var(--size-2)" }}
            nativeBehavior="present"
            nativeStencil={{
              propsJSON: "{}",
              type: "notebook",
            }}
          >
            {t("note.cta.details")}
          </Link>
        )}
      </div>

      <NoteActionsModal
        actions={computeActions()}
        color={color}
        isOpen={isActionsOpen}
        postTitle={modalPostTitle}
        title={modalTitle}
        onColorChange={onColorChange}
        onRequestClose={handleRequestCloseSheet}
      />
    </>
  );
}
